import { bandScore } from "@/lib/score";
import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/SidePanel";
import { Radio, Check, MapPin, Megaphone, Loader2, Zap } from "lucide-react";
import { HAZARD_LABEL, HAZARD_BASE } from "@/lib/hazard";
import { ExposureBlock } from "@/components/ExposureBlock";
import { ImpactBadge } from "@/components/ImpactBadge";

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "UTC",
    }) + "Z";
  } catch {
    return "";
  }
};

const AlertRow = ({ a, onSelect, onAck, onBroadcast }) => {
  const [busy, setBusy] = useState(false);
  const critical = a.level === "critical";
  const color = HAZARD_BASE[a.hazard] || "#00F0FF";
  const handleBroadcast = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await onBroadcast?.(a.uid);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className={`p-3 border-b border-white/5 border-l-2 ${a.acknowledged ? "opacity-45" : ""}`}
      style={{ borderLeftColor: critical ? "#FF2A2A" : "#FF9500" }}
      data-testid={`alert-row-${a.uid}`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
          <span className={`text-[9px] font-mono uppercase tracking-widest ${critical ? "text-atlas-red" : "text-atlas-orange"}`}>
            {critical ? "CRITICAL" : "WATCH"} · {HAZARD_LABEL[a.hazard] || a.hazard}
          </span>
        </div>
        <span className="text-[10px] font-mono text-atlas-cyan text-right leading-tight">
          {Math.round(bandScore(a).v * 100)}%
          <span className="block text-[8px] text-atlas-secondary/75">
            {String(a.calibration_source || "").startsWith("rule:") ? "Rule alert" : bandScore(a).src}
          </span>
        </span>
      </div>
      <div className="text-sm font-display text-white leading-tight mb-0.5">{a.title}</div>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-[11px] font-mono text-atlas-secondary/80">{a.region}</span>
        <ImpactBadge impact={a.impact} testId={`alert-impact-${a.uid}`} />
      </div>
      <p className="text-[11px] text-atlas-secondary leading-relaxed mb-2">{a.plain_summary || a.statement}</p>
      {a.exposure && (
        <ExposureBlock exposure={a.exposure} variant="compact" testId={`alert-exposure-${a.uid}`} />
      )}
      {a.broadcast_eligible && (
        <div
          className="mt-1.5 flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider text-atlas-orange"
          data-testid={`alert-leadtime-${a.uid}`}
          title={a.lead_time || "Escalating toward the alert level within 24 hours"}
        >
          <Zap className="w-3 h-3" /> Escalating within 24h · broadcast-ready
        </div>
      )}
      <div className="flex items-center justify-between mt-2">
        <span className="text-[9px] font-mono text-atlas-secondary/75">{fmtTime(a.created_at)}</span>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => onSelect(a)}
            data-testid={`alert-focus-${a.uid}`}
            className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider px-2 h-6 border border-atlas-cyan/40 text-atlas-cyan hover:bg-atlas-cyan/10 transition-colors"
          >
            <MapPin className="w-3 h-3" /> Focus
          </button>
          {a.broadcast_eligible && (
            <button
              onClick={handleBroadcast}
              disabled={busy}
              data-testid={`alert-broadcast-${a.uid}`}
              className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider px-2 h-6 border border-atlas-orange/40 text-atlas-orange hover:bg-atlas-orange/10 disabled:opacity-40 transition-colors"
              title="Generate an Vigilo Earth image + caption and broadcast this breakthrough"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Megaphone className="w-3 h-3" />} Broadcast
            </button>
          )}
          {!a.acknowledged && (
            <button
              onClick={() => onAck(a.uid)}
              data-testid={`alert-ack-${a.uid}`}
              className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider px-2 h-6 border border-white/15 text-atlas-secondary hover:text-white transition-colors"
            >
              <Check className="w-3 h-3" /> Ack
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const AlertsDrawer = ({ open, onOpenChange, alerts = [], onSelect, onAck, onBroadcast }) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent
      side="right"
      aria-describedby={undefined}
      className="w-full sm:max-w-md bg-atlas-surface border-l border-white/10 p-0 text-atlas-foreground"
      data-testid="alerts-drawer"
    >
      <SheetHeader className="h-14 px-4 flex flex-row items-center justify-between border-b border-white/10 space-y-0">
        <SheetTitle className="flex items-center gap-2 text-white font-display">
          <Radio className="w-4 h-4 text-atlas-red" />
          Early-Warning Broadcast
        </SheetTitle>
      </SheetHeader>
      <div className="h-[calc(100vh-3.5rem)] overflow-y-auto">
        {alerts.length === 0 ? (
          <div className="flex items-center justify-center h-40 px-6 text-center">
            <span className="text-[11px] font-mono text-atlas-secondary/75 uppercase tracking-widest">
              No breakthroughs yet. Alerts fire when a hypothesis exceeds 75% confidence.
            </span>
          </div>
        ) : (
          <div data-testid="alerts-list">
            {alerts.map((a) => (
              <AlertRow key={a.uid} a={a} onSelect={onSelect} onAck={onAck} onBroadcast={onBroadcast} />
            ))}
          </div>
        )}
      </div>
    </SheetContent>
  </Sheet>
);
